import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import CardImage from '../../../ui/CardImage';
import CardOverlays from '../../../ui/CardOverlays';
import Badge from '../../../ui/Badge';
import { useCloseOnBack } from '../../../ui/backLayer/useCloseOnBack';
import { DROP_IDS, dragData, dragId } from '../hooks/usePlaytestDnd';
import { cardImageProps, otherFace } from '../utils/cardView';
import { mergeHandlers } from '../utils/eventHandlers';
import './HandFan.css';

/** Neigung und Absinken der äußersten Karte; die Karten dazwischen folgen einem Bogen. */
const EDGE_ANGLE_DEG = 6;
const EDGE_DROP_PX = 16;

/** So lange nach dem senkenden Tipp wird dessen Klick abgefangen. */
const OUTSIDE_TAP_CLICK_MS = 600;

/**
 * Handfächer nach Arena-Vorbild (Spec 2026-10-21 §5.2), reine Darstellung.
 *
 *   <BoardDock raised={shown} onRaisedChange={setRaised} isMobile={isMobile} tiles={…}
 *     hand={<HandFan cards={hand} raised={shown} onRaisedChange={setRaised} closeOnBack={isMobile && raised}
 *       isMobile={isMobile} canDrag onCardOpenMenu={(card, { x, y }) => …} previewBind={hover.bind} />} />
 *
 * - Der Fächer verschiebt sich nicht selbst: in Ruhe senkt `BoardDock` ihn samt Zonen-Kacheln
 *   halb unter den Rand, und das Dock schaltet `raised` per Zeiger bzw. Tipp. `raised` (gehört
 *   der Seite) braucht der Fächer nur für die Karten: am Handy hebt das Herausheben einer Karte
 *   das Dock mit an (`onRaisedChange(true)`).
 * - `closeOnBack`: das Dock ist auf Wunsch des Nutzers gehoben, die Zurück-Geste senkt es
 *   (`onRaisedChange(false)`). Getrennt von `raised`, weil die Seite das Dock auch während eines
 *   Zugs anzeigt — dafür darf kein Zurück-Eintrag entstehen, sonst legte jeder Zug einen an.
 * - Handy: Tipp auf eine Karte hebt sie groß (Bild 220 px, in niedrigen Fenstern so viel kleiner,
 *   dass sie über dem angehobenen Fächer ganz sichtbar bleibt) über die Mitte des Fächers
 *   (`data-lifted`; sie ist dann selbst das Drag-Element, ein Platzhalter hält ihren Platz),
 *   der Rest der Hand bleibt darunter; Tipp daneben — im Fächer oder irgendwo
 *   sonst — senkt sie wieder, ohne dabei den Fächer umzuschalten; ein Tipp außerhalb löst dort
 *   sonst nichts aus (kein Tappen der Brettkarte darunter). Ist sie angehoben, schließt
 *   die Zurück-Geste zuerst sie, dann das gehobene Dock (`useCloseOnBack`; beide hier
 *   angemeldet, damit die Reihenfolge auch gilt, wenn ein Tipp Karte und Dock zugleich hebt).
 * - Jede Karte ist ziehbar (`dragData(card, 'hand')`; `canDrag={false}` sperrt das für
 *   Ausgeschiedene, die nur zusehen), trägt `layoutId = instanceId` (gleitet
 *   zwischen Hand und Brett) und hebt sich unter dem Zeiger um 16 px an, ohne Skalierung — die
 *   Nachbarn bleiben liegen. Lesen geschieht über die Vorschau: `previewBind(card,
 *   { placement: 'above' })` liefert deren Zeiger-Handler (`useHoverPreview.bind`).
 * - Rechtsklick (am Handy auch der Langdruck des Browsers) → `onCardOpenMenu(card, { x, y })`
 *   in Client-Koordinaten; was sich öffnet, entscheidet die Seite.
 * - Ablageziel `DROP_IDS.hand` ist das Fächer-Element, nicht die Hülle. Es zählt nur dort, wo es
 *   zu sehen ist, weil die Seite Ablageziele samt Transform misst (`DROP_MEASURING` in
 *   `usePlaytestDnd`): im gesenkten Dock also nur die sichtbare Hälfte. Hebt oder senkt sich das
 *   Dock während eines Zugs, folgt das Ziel mit der nächsten Messung (`DROP_REMEASURE_MS` nach
 *   einer Bewegung); der Übergang des Docks bleibt, weil eine herausgehobene Karte, die sofort
 *   mitspränge, beim Zugstart an falscher Stelle gemessen würde. `data-over` an der Hülle
 *   während eines Zugs.
 * - Zeiger-Ereignisse fängt nur die Kartenreihe, die Hülle lässt sie durch (das Dock schwebt
 *   über dem Brett).
 * - Eine verdeckte Handkarte trägt oben das Abzeichen „Verdeckt" (der Besitzer sieht ihr Bild).
 * - Zähler links oben (`Badge`, Optik der Zonen-Zähler), weil der halb verdeckte Fächer nicht
 *   zählbar ist.
 * - Kartenbreite über die CSS-Variable `--hand-fan-card-w` (150 px; im Dock setzt `BoardDock`
 *   sie, damit Handkarten und Zonen-Kacheln gleich groß sind); die Überlappung wächst mit der
 *   Kartenzahl, damit der Fächer in die Breite seines Containers passt.
 * - Die Hand zeigt die echte Seite jeder Karte (`transformedFace`). Der Umdreh-Knopf erscheint nur
 *   auf der Karte, deren Großansicht steht (Rechner: `previewFlipFor(card)` liefert ihre Funktion
 *   oder `undefined`), bzw. auf der angehobenen Karte (Handy); er dreht nur die Ansicht, nie die
 *   Karte. Am Handy gilt die gedrehte Ansicht, bis die Karte sinkt (jedes Senken stellt die echte
 *   Seite wieder her).
 * - Prüfanker: `data-testid="hand-fan"` mit `data-raised`, `data-testid="hand-card"` mit
 *   `data-instance-id`/`data-lifted`, `data-testid="hand-fan-count"`.
 */
export default function HandFan({
  cards, raised, onRaisedChange, closeOnBack = false, isMobile, canDrag = true, onCardOpenMenu, previewBind, previewFlipFor,
}) {
  const rootRef = useRef(null);
  const [liftedId, setLiftedId] = useState(null);
  const [liftedOtherSide, setLiftedOtherSide] = useState(false);
  const { isOver, setNodeRef: setDropRef } = useDroppable({ id: DROP_IDS.hand });

  const lower = useCallback(() => {
    setLiftedId(null);
    setLiftedOtherSide(false);
  }, []);

  const lifted = isMobile && cards.some((card) => card.instanceId === liftedId) ? liftedId : null;

  // Verlässt die angehobene Karte die Hand, darf sie bei ihrer Rückkehr nicht wieder angehoben sein.
  useEffect(() => {
    if (liftedId !== null && lifted === null) lower();
  }, [liftedId, lifted, lower]);

  // Tipp außerhalb des Fächers senkt die Karte und tut sonst nichts: der Klick desselben Tipps
  // wird verschluckt, sonst tappte er nebenbei die Brettkarte darunter. Tipps im Fächer erledigt
  // dessen onClick.
  useEffect(() => {
    if (lifted === null) return undefined;
    const swallowClick = (event) => {
      event.stopPropagation();
      event.preventDefault();
    };
    const handlePointerDown = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      lower();
      // Folgt kein Klick (der Finger hat geschwenkt), darf der Wächter den nächsten Tipp nicht
      // schlucken: er endet mit der nächsten Berührung oder nach der Frist.
      const release = () => document.removeEventListener('click', swallowClick, true);
      document.addEventListener('click', swallowClick, { capture: true, once: true });
      document.addEventListener('pointerdown', release, { capture: true, once: true });
      setTimeout(release, OUTSIDE_TAP_CLICK_MS);
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [lifted, lower]);

  useCloseOnBack(closeOnBack, () => onRaisedChange?.(false));
  useCloseOnBack(lifted !== null, lower);

  // Tipp in den Fächer neben die angehobene Karte senkt nur sie; das Dock darüber bleibt, wie es ist.
  const handleAreaClick = (event) => {
    if (lifted === null) return;
    event.stopPropagation();
    lower();
  };

  const liftCard = (instanceId) => {
    setLiftedId(instanceId);
    setLiftedOtherSide(false);
    if (!raised) onRaisedChange?.(true);
  };

  const count = cards.length;
  const maxOffset = Math.max((count - 1) / 2, 1);

  return (
    <div
      ref={rootRef}
      className="hand-fan"
      data-testid="hand-fan"
      data-raised={raised ? 'true' : 'false'}
      data-over={isOver ? 'true' : undefined}
      onClick={isMobile ? handleAreaClick : undefined}
    >
      <div className="hand-fan__fan" ref={setDropRef}>
        <div
          className="hand-fan__row"
          style={{
            '--hand-fan-count': count,
            '--hand-fan-angle': `${EDGE_ANGLE_DEG / maxOffset}deg`,
            '--hand-fan-arc': `${EDGE_DROP_PX / (maxOffset * maxOffset)}px`,
          }}
        >
          <span className="hand-fan__count">
            <Badge tone="default" size="md" data-testid="hand-fan-count">{count}</Badge>
          </span>
          {cards.map((card, index) => (
            <HandCard
              key={card.instanceId}
              card={card}
              offset={index - (count - 1) / 2}
              layoutKey={`${index}/${count}/${lifted === card.instanceId}`}
              lifted={lifted === card.instanceId}
              canDrag={canDrag}
              face={lifted === card.instanceId && liftedOtherSide
                ? otherFace(cardImageProps(card).face) : cardImageProps(card).face}
              onFlip={lifted === card.instanceId
                ? () => setLiftedOtherSide((other) => !other) : previewFlipFor?.(card)}
              previewHandlers={previewBind ? previewBind(card, { placement: 'above' }) : null}
              onTap={isMobile ? liftCard : undefined}
              onOpenMenu={onCardOpenMenu}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function HandCard({ card, offset, layoutKey, lifted, canDrag, face, onFlip, previewHandlers, onTap, onOpenMenu }) {
  const { instanceId } = card;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dragId(instanceId),
    data: dragData(card, 'hand'),
    disabled: !canDrag,
  });
  const handlers = mergeHandlers(listeners, previewHandlers);
  const { src, backSrc, alt } = cardImageProps(card);

  const handleClick = (e) => {
    if (!onTap) return;
    e.stopPropagation();
    onTap(instanceId);
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onOpenMenu?.(card, { x: e.clientX, y: e.clientY });
  };

  // Die herausgehobene Karte ist selbst das Drag-Element: dnd-kit misst den Knoten von
  // `setNodeRef`, und nur wenn der dort liegt, wo der Finger greift, stimmen Overlay und
  // Ablageposition. Ihren Platz im Fächer hält ein Platzhalter.
  return (
    <>
      {lifted && <div className="hand-fan__slot" data-testid="hand-card-slot" aria-hidden />}
      <motion.div
        ref={setNodeRef}
        layoutId={instanceId}
        layoutDependency={layoutKey}
        className={`hand-fan__card${lifted ? ' hand-fan__card--lifted' : ''}${isDragging ? ' hand-fan__card--dragging' : ''}`}
        style={{ '--hand-card-offset': offset }}
        data-testid="hand-card"
        data-instance-id={instanceId}
        data-lifted={lifted ? 'true' : undefined}
        {...attributes}
        {...handlers}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
      >
        <div className="hand-fan__card-body">
          <CardImage src={src} backSrc={backSrc} alt={alt} face={face} onFlip={onFlip}>
            {card.faceDown && (
              <CardOverlays>
                <CardOverlays.Corner position="top-left">
                  <CardOverlays.Badge passive>Verdeckt</CardOverlays.Badge>
                </CardOverlays.Corner>
              </CardOverlays>
            )}
          </CardImage>
        </div>
      </motion.div>
    </>
  );
}
